import React from 'react';

import {useStore} from 'effector-react';
import {useTranslation} from 'react-i18next';
import {generatePath} from 'react-router-dom';

import {exportBlockingAuditLog, fetchBlockingAuditLog} from 'api/blockingAudit';
import {auditColumns} from 'components/common/AuditLog/columns';
import {AuditExportControl} from 'components/common/AuditLog/ExportControl';
import {AuditNavigation} from 'components/common/AuditLog/Navigation';
import {AuditPeriodFilter} from 'components/common/AuditLog/PeriodFilter';
import {AuditReference} from 'components/common/AuditLog/Reference';
import {BreakAllValue, PageContent, Toolbar} from 'components/common/AuditLog/styled';
import {useAuditLog} from 'components/common/AuditLog/useAuditLog';
import {Page} from 'components/common/Page';
import {formatLocalDateTime, translateUserRole} from 'components/pages/RecallAudit/utils';
import {Input} from 'components/ui-kit/Input';
import {Link} from 'components/ui-kit/Link';
import {Select} from 'components/ui-kit/Select';
import {mapTableColumns, Table} from 'components/ui-kit/Table2.0';
import {PERMISSION_ALIASES} from 'core/constants/permissionAliases';
import {ROUTE} from 'core/routes';
import {permissions$} from 'models/auth';
import {BLOCKING_AUDIT_EVENTS} from 'types/blocking-audit';
import {BlockingSourceFormation, BlokcingReason, UnblockingReason} from 'types/products-blocking';

import {mapBlockingAuditFilters} from './filters';

export {BlockingAuditDetailPage} from './Detail';

const selectFilters = [
    {id: 'reason', values: Object.values(BlokcingReason), dictionary: 'blockingReason'},
    {
        id: 'unblockingReason',
        values: Object.values(UnblockingReason),
        dictionary: 'unblockingReason',
    },
    {
        id: 'sourceFormation',
        values: Object.values(BlockingSourceFormation),
        dictionary: 'blockingSourceFormation',
    },
];

export const BlockingAuditLogPage = () => {
    const {t} = useTranslation();
    const permissions = useStore(permissions$);
    const {
        filters,
        page,
        size,
        data,
        pending,
        setPage,
        setSize,
        changeFilters,
        resetFilters,
    } = useAuditLog(fetchBlockingAuditLog, mapBlockingAuditFilters);
    const fields = auditColumns(
        t,
        BLOCKING_AUDIT_EVENTS.map((value) => ({
            value,
            label: t(`pages.blockingAudit.events.${value}`),
        })),
    ).map((field) =>
        field.id === 'eventTimestamp' ? {...field, filter: <AuditPeriodFilter />} : field,
    );
    fields.push(
        ...['markingCode', 'reason', 'documentId', 'blockingId'].map((id) => ({
            id,
            header: t(`pages.blockingAudit.fields.${id}`),
            width: 240,
            filter:
                id === 'reason' ? (
                    <Select
                        isClearable
                        options={Object.values(BlokcingReason).map((value) => ({
                            value,
                            label: t(`dictionaries.products.blockingReason.${value}`),
                        }))}
                    />
                ) : (
                    <Input />
                ),
        })),
    );
    fields.push(
        ...selectFilters
            .filter(({id}) => id !== 'reason')
            .map(({id, values, dictionary}) => ({
                id,
                header: t(`pages.blockingAudit.fields.${id}`),
                width: 220,
                filter: (
                    <Select
                        isClearable
                        options={values.map((value) => ({
                            value,
                            label: t(`dictionaries.products.${dictionary}.${value}`),
                        }))}
                    />
                ),
            })),
    );
    const rows = (data?.data || []).map((entry) => ({
        ...entry,
        entry,
        eventId: (
            <Link to={generatePath(ROUTE.BLOCKING_AUDIT_DETAIL, {id: entry.eventId})}>
                {entry.eventId}
            </Link>
        ),
        eventType: t(`pages.blockingAudit.events.${entry.eventType}`, {
            defaultValue: entry.eventType,
        }),
        eventTimestamp: formatLocalDateTime(entry.eventTimestamp),
        userRole: translateUserRole(t, entry.userRole),
        userFullName: entry.userFullName || '—',
        organizationName: entry.organizationName ?? entry.organizationId ?? '—',
        organizationTin: entry.organizationTin || '—',
        documentId: <AuditReference field="documentId" value={entry.documentId} />,
        ...Object.fromEntries(
            selectFilters
                .filter(({id}) => id !== 'reason')
                .map(({id, dictionary}) => {
                    const value = entry[id as keyof typeof entry];
                    return [
                        id,
                        value
                            ? t(`dictionaries.products.${dictionary}.${value}`, {
                                  defaultValue: String(value),
                              })
                            : '—',
                    ];
                }),
        ),
        blockingId: entry.blockingId || '—',
        markingCode: (
            <BreakAllValue>
                {<AuditReference field="markingCode" value={entry.markingCode} />}
            </BreakAllValue>
        ),
        reason: entry.reason
            ? t(`dictionaries.products.blockingReason.${entry.reason}`, {
                  defaultValue: entry.reason,
              })
            : '—',
    }));
    return (
        <Page title={t('menu.activityLog')}>
            <PageContent>
                <AuditNavigation>
                    <Toolbar>
                        {permissions.includes(PERMISSION_ALIASES.blockingAuditLogExport) && (
                            <AuditExportControl
                                fileName="blocking-audit"
                                initialPeriod={filters.eventTimestamp as [Date | null, Date | null]}
                                onExport={(format, period) => {
                                    const params = mapBlockingAuditFilters({
                                        ...filters,
                                        eventTimestamp: period,
                                    });
                                    return exportBlockingAuditLog({
                                        ...params,
                                        from: params.from!,
                                        to: params.to!,
                                        page,
                                        size,
                                        format,
                                    });
                                }}
                            />
                        )}
                    </Toolbar>
                </AuditNavigation>
                <Table
                    hideSetting
                    columns={mapTableColumns(fields, filters, changeFilters, resetFilters)}
                    data={rows}
                    pageIndex={page}
                    pageCount={data?.totalPages}
                    totalRowsCount={data?.totalElements}
                    onPageChange={setPage}
                    onPageSizeChange={(value) => {
                        setSize(value);
                        setPage(0);
                    }}
                    isLoading={pending}
                />
            </PageContent>
        </Page>
    );
};
